import React from 'react';
import useGameStore from '../../store/gameStore';
import { EMPTY_OBJECT } from '../../store/stableEmpties';
import useAudioStore from '../../store/audioStore';
import { formatMoney, severityBg, severityColor } from '../../utils/format';
import { AlertTriangle, Zap, CloudRain, Users, Car, FileText, TrendingUp, UserX, Gift, Truck, Landmark, Megaphone, Clock } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { localize } from '../../data/i18nData';
import { VEHICLES } from '../../data/vehicles';
import MarkdownLite from '../MarkdownLite';

const categoryIcon = (category) => {
  const icons = {
    'Panne mécanique': Car, // i18n-keep — data category key
    'Météo': CloudRain, // i18n-keep
    'Social / Grève': Users, // i18n-keep
    'Accident': AlertTriangle,
    'Administratif': FileText,
    'Marché / Économie': TrendingUp, // i18n-keep
    'RH interne': UserX,
    'Opportunité': Gift, // i18n-keep
    'Véhicule occasion': Truck, // i18n-keep
    'Macro-économie': Landmark, // i18n-keep
  };
  return icons[category] || Zap;
};

export const EventPopup = () => {
  const { t } = useTranslation();
  const { pendingEvents, resolveEvent, resolveEventCrisisComm, treasury, hasSupportUnlock, postponeEvent } = useGameStore();
  const totalDays = useGameStore(s => s.totalDays || 0);
  // 08/02/2026 — Q2 Lot A : on lit `hiredSupport` pour filtrer les choix
  // `requiresInternalGarage` (Mehdi/Bruno).
  const hiredSupport = useGameStore(s => s.hiredSupport || EMPTY_OBJECT);
  const state = { hiredSupport };
  const playSound = useAudioStore((s) => s.play);

  // Only surface events whose `scheduledFor` (if set) has elapsed. Events
  // without `scheduledFor` are always immediate. Used by the fuel-theft arc
  // and other deferred follow-ups so the audit result fires 7-14 days after
  // the player approves the audit, not the same day.
  const rawEvent = React.useMemo(() => {
    return (pendingEvents || []).find(e => !e.scheduledFor || e.scheduledFor <= totalDays) || null;
  }, [pendingEvents, totalDays]);
  // Localize the event (keep category icon lookup using the raw FR category)
  const event = rawEvent ? localize('event', rawEvent) : null;

  // Illustration fallback flag
  const [illustrationOk, setIllustrationOk] = React.useState(true);
  React.useEffect(() => { setIllustrationOk(true); }, [event?.id]);

  // Play event sound when popup appears. We deliberately gate on
  // `instanceId` only so the sound fires once per new event, not when
  // the event prop's object identity shifts for the same instance.
  React.useEffect(() => {
    if (event) playSound('event');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.instanceId, playSound]);

  if (!event) return null;
  const Icon = categoryIcon(rawEvent.category);
  const hasCrisisComm = typeof hasSupportUnlock === 'function' && hasSupportUnlock('crisis_comm');
  const hasNegativeRepChoice = (rawEvent.choices || []).some(c => (c.reputationChange || 0) < 0);
  const showCrisisComm = hasCrisisComm && hasNegativeRepChoice && treasury >= 500;
  // Postpone is offered ONLY on vehicle-purchase / vehicle-sale opportunities
  // (occasion, enchère, liquidation, proposition de rachat). Those decisions
  // benefit from a quick fleet review before committing.
  const canPostpone = !!rawEvent.isUsedVehicleEvent;

  // Illustration path (one PNG per event id, optional — silently hidden if missing)
  const illustrationSrc = event.id ? `/events/${event.id}.webp` : null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="event-popup-overlay">
      <div
        className="bg-ink-800 rounded-xl w-full max-w-md max-h-[90vh] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300 flex flex-col"
        data-testid="event-popup"
      >
        {/* Illustration (BD semi-réaliste) — only if file exists */}
        {illustrationSrc && illustrationOk && (
          <div className="relative w-full bg-slate-900 overflow-hidden shrink-0" style={{ aspectRatio: '16 / 9' }}>
            <img
              src={illustrationSrc}
              alt={event.name}
              onError={() => setIllustrationOk(false)}
              className="w-full h-full object-cover"
              data-testid="event-illustration"
            />
            {/* Gradient overlay for header readability */}
            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
          </div>
        )}

        {/* Header — sticky-like, doesn't scroll */}
        <div className={`${severityBg(event.severity, rawEvent.category)} px-5 py-4 flex items-center gap-3 shrink-0`}>
          <Icon className="w-6 h-6 text-white" />
          <div className="min-w-0">
            <p className="text-white/80 text-xs font-medium uppercase tracking-wider">{event.category}</p>
            <h3 className="text-white font-bold text-lg truncate">{event.name}</h3>
          </div>
        </div>

        {/* Body — scrollable on Mac/PC (02/06/2026 fix : long events with
            4-5 choices + crisisComm + postpone were cut at the bottom on
            laptops 1280x800. Now the body scrolls within the 90vh frame). */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0" data-testid="event-popup-body">
          <p className="text-slate-400 text-sm leading-relaxed">{event.revenueEffect}</p>

          <div className="flex gap-4 text-sm">
            {event.costMin > 0 && (
              <div className="bg-danger/10 rounded-lg px-3 py-2">
                <p className="text-danger font-semibold">{formatMoney(event.costMin)} - {formatMoney(event.costMax)}</p>
                <p className="text-danger text-xs">{t('estimated_cost')}</p>
              </div>
            )}
            {event.durationDays > 0 && (
              <div className="bg-warn/10 rounded-lg px-3 py-2">
                <p className="text-warn font-semibold">{event.durationDays} {event.durationDays > 1 ? t('event_days_plural') : t('event_days_singular')}</p>
                <p className="text-warn text-xs">{t('impact_duration')}</p>
              </div>
            )}
          </div>

          {/* Choices */}
          <div className="space-y-2 pt-2">
            {/* 08/02/2026 — Q2 Lot A user prod : filtre les choix marqués
                `requiresInternalGarage` si ni Mehdi ni Bruno ne sont
                embauchés. Sinon le joueur voit une option qu'il ne peut
                pas exécuter (UX confuse). */}
            {event.choices
              .filter(c => {
                if (!c.requiresInternalGarage) return true;
                const hired = state?.hiredSupport || {};
                return !!(hired['role-mehdi-ferhat'] || hired['role-bruno-lavergne']);
              })
              .map((choice, idx) => {
              // Compute the real cost of this choice
              let realCost = choice.cost || 0;
              if (choice.specialAction?.type === 'buyUsedVehicle') {
                const vd = VEHICLES.find(v => v.id === choice.specialAction.vehicleDataId);
                if (vd) realCost = Math.round(vd.price * choice.specialAction.discount);
              }
              const treasuryAfter = treasury - realCost;
              const willGoNegative = realCost > 0 && treasuryAfter < 0;
              // Estimated daily agios on negative treasury: max(5, |after| * 0.0005)
              const dailyAgios = willGoNegative
                ? Math.max(5, Math.round(Math.abs(treasuryAfter) * 0.0005))
                : 0;
              return (
              <button
                key={`${event.instanceId}-choice-${idx}-${choice.label}`}
                onClick={() => resolveEvent(event.instanceId, idx)}
                className="w-full text-left border border-ink-600 rounded-lg p-3 hover:border-accent hover:bg-accent/5 transition-all group"
                data-testid={`event-choice-${idx}`}
              >
                <div className="flex justify-between items-start">
                  <span className="font-medium text-slate-200 text-sm group-hover:text-foreground">{choice.label}</span>
                  {realCost > 0 && (
                    <span className="text-danger text-xs font-semibold">-{formatMoney(realCost)}</span>
                  )}
                </div>
                <p className="text-slate-500 text-xs mt-1">{choice.description}</p>
                {choice.reputationChange !== 0 && (
                  <span className={`text-xs mt-1 inline-block ${choice.reputationChange > 0 ? 'text-money' : 'text-danger'}`}>
                    {t('event_reputation_label')} : {choice.reputationChange > 0 ? '+' : ''}{choice.reputationChange} {t('event_pts_suffix')}
                  </span>
                )}
                {realCost > 0 && (
                  <div className={`mt-2 rounded-md border px-2.5 py-1.5 text-[11px] ${
                    willGoNegative ? 'border-danger/40 bg-danger/5' : 'border-ink-600 bg-ink-700/30'
                  }`}>
                    <div className="flex items-center justify-between gap-2 font-mono">
                      <span className="text-slate-500 uppercase tracking-wider text-[9px]">{t('event_treasury_after')}</span>
                      <span className={`font-bold tabular-nums ${willGoNegative ? 'text-danger' : 'text-money'}`}>
                        {formatMoney(treasuryAfter)}
                      </span>
                    </div>
                    {willGoNegative && (
                      <>
                        <div className="flex items-center justify-between gap-2 font-mono mt-1">
                          <span className="text-slate-500 uppercase tracking-wider text-[9px]">{t('event_estimated_agios')}</span>
                          <span className="text-warn font-bold tabular-nums">~{formatMoney(dailyAgios)}{t('event_per_day_suffix')}</span>
                        </div>
                        <div className="mt-1.5 flex items-start gap-1.5 pt-1.5 border-t border-danger/20">
                          <Landmark className="w-3 h-3 text-warn flex-shrink-0 mt-0.5" />
                          <p className="text-warn text-[10px] leading-snug">
                            {t('event_contact_bank_tip')}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </button>
              );
            })}
            {showCrisisComm && (
              <button
                onClick={() => { playSound('event'); resolveEventCrisisComm(event.instanceId); }}
                className="w-full text-left border-2 border-purple-500/50 bg-purple-500/5 rounded-lg p-3 hover:border-purple-500 hover:bg-purple-500/10 transition-all"
                data-testid="event-crisis-comm"
              >
                <div className="flex justify-between items-start">
                  <span className="font-medium text-purple-300 text-sm flex items-center gap-1.5">
                    <Megaphone className="w-4 h-4" /> {t('event_crisis_comm_label')}
                  </span>
                  <span className="text-danger text-xs font-semibold">-500 €</span>
                </div>
                <p className="text-slate-400 text-xs mt-1">
                  <MarkdownLite as="span" text={t('event_crisis_comm_desc')} />
                </p>
              </button>
            )}
            {/* Postpone — vehicle-purchase opportunities only. Closes the
                modal without resolving the event so the player can browse
                their fleet and come back via the floating reminder badge. */}
            {canPostpone && (
              <button
                onClick={() => postponeEvent(event.instanceId)}
                className="w-full text-left border border-amber-500/40 bg-amber-500/5 rounded-lg p-3 hover:border-amber-500 hover:bg-amber-500/10 transition-all flex items-start gap-2"
                data-testid="event-postpone"
              >
                <Clock className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-amber-200 text-sm">{t('event_postpone_title')}</p>
                  <p className="text-slate-400 text-xs mt-0.5">{t('event_postpone_desc')}</p>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventPopup;
